/**
 * cryptoHistoricalPriceCache — fetch + cache des prix historiques EUR
 * pour les besoins du calculator Tax FR (V2.E2 priceSnapshots).
 *
 * Wraps `/coins/{id}/market_chart/range` de CoinGecko via le proxy
 * existant (`callCoinGeckoAPI`). Cache localStorage **sans TTL** car
 * les prix historiques sont immutables — le cours BTC le 15 mai 2024
 * ne changera plus. Quota négligeable : ~30 bytes par entrée.
 *
 * Architecture :
 *  - Clé cache : `${coinId}|${dateISO}` (YYYY-MM-DD).
 *  - Match exact sur la date demandée (CoinGecko renvoie souvent un
 *    point par jour).
 *  - Fallback graceful : si l'API échoue ou ne renvoie rien pour une
 *    date, on retourne `null` plutôt que de throw — le caller décide
 *    de surfacer un warning.
 *
 * Créé en Sprint V2.E.UI.4.
 */

import { callCoinGeckoAPI } from './api';

const STORAGE_KEY = 'crypto_historical_price_cache_v1';

/** In-memory mirror of localStorage to avoid repeated parses. */
let memoryCache: Record<string, number> | null = null;

function loadCache(): Record<string, number> {
  if (memoryCache) return memoryCache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      memoryCache = {};
      return memoryCache;
    }
    const parsed = JSON.parse(raw);
    memoryCache = typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    memoryCache = {};
  }
  return memoryCache;
}

function persistCache(): void {
  if (!memoryCache) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryCache));
  } catch {
    // Quota exceeded or private mode — silent.
  }
}

function makeKey(coinId: string, dateISO: string): string {
  return `${coinId}|${dateISO}`;
}

/** Convert ISO date YYYY-MM-DD to a UNIX timestamp (00:00 UTC). */
function dateISOToUnix(dateISO: string): number {
  return Math.floor(new Date(`${dateISO}T00:00:00Z`).getTime() / 1000);
}

/**
 * Fetch a single (coinId, date) historical price in EUR. Returns the
 * cached value if hit, otherwise queries CoinGecko range API for a
 * 1-day window centred on the date.
 *
 * Returns `null` on failure (network error, no data, malformed
 * response). Never throws.
 */
export async function getHistoricalPriceEUR(
  coinId: string,
  dateISO: string,
): Promise<number | null> {
  const cache = loadCache();
  const key = makeKey(coinId, dateISO);

  if (typeof cache[key] === 'number' && cache[key] > 0) {
    return cache[key];
  }

  try {
    const fromUnix = dateISOToUnix(dateISO);
    const toUnix = fromUnix + 86400; // +1 day
    const data = await callCoinGeckoAPI(
      `/coins/${encodeURIComponent(coinId)}/market_chart/range`,
      { vs_currency: 'eur', from: fromUnix, to: toUnix },
    );

    // Response shape: { prices: [[unix_ms, price_eur], ...], ... }
    const prices: Array<[number, number]> | undefined = data?.prices;
    if (!Array.isArray(prices) || prices.length === 0) return null;

    // Pick the entry closest to fromUnix (start of day). CoinGecko
    // sometimes returns 2 points for a 1-day window — first is more
    // reliable for "price at this date".
    const targetMs = fromUnix * 1000;
    let best = prices[0];
    let bestDelta = Math.abs(prices[0][0] - targetMs);
    for (const entry of prices.slice(1)) {
      const delta = Math.abs(entry[0] - targetMs);
      if (delta < bestDelta) {
        best = entry;
        bestDelta = delta;
      }
    }
    const priceEUR = best[1];
    if (typeof priceEUR !== 'number' || priceEUR <= 0) return null;

    cache[key] = priceEUR;
    persistCache();
    return priceEUR;
  } catch {
    return null;
  }
}

export interface PriceSnapshotsResult {
  /** Final snapshots map ready to pass to `calculateForYear`. */
  snapshots: Record<string, Record<string, number>>;
  /** Number of (asset, date) pairs requested. */
  requested: number;
  /** Number resolved (cache hit OR fresh fetch). */
  resolved: number;
  /** Number that came back null (API failure or no data). */
  missed: number;
  /** True when all requested pairs failed — UI shows a hard error. */
  totalFailure: boolean;
  /** List of unresolved pairs for user-facing diagnostics. */
  missedPairs: Array<{ coinId: string; dateISO: string }>;
}

/**
 * Fetch historical prices for a batch of (coinId, dateISO) pairs.
 * Returns a structured result the UI can use to drive its banner
 * (success / partial / total failure).
 *
 * Pairs already in cache are returned without network round-trip.
 * Misses are fetched in series (CoinGecko free tier rate-limits at
 * ~30 req/min — parallel would only burn the budget faster). The
 * caller is expected to debounce by gating the call behind an
 * explicit user action (per V2.E.UI.4 design — bouton explicite).
 */
export async function fetchHistoricalPricesBatch(
  pairs: Array<{ coinId: string; dateISO: string }>,
): Promise<PriceSnapshotsResult> {
  const snapshots: Record<string, Record<string, number>> = {};
  const missedPairs: Array<{ coinId: string; dateISO: string }> = [];
  let resolved = 0;

  // Deduplicate pairs (caller may pass duplicates).
  const seen = new Set<string>();
  const uniquePairs = pairs.filter((p) => {
    const k = makeKey(p.coinId, p.dateISO);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  for (const { coinId, dateISO } of uniquePairs) {
    const price = await getHistoricalPriceEUR(coinId, dateISO);
    if (price === null) {
      missedPairs.push({ coinId, dateISO });
      continue;
    }
    if (!snapshots[coinId]) snapshots[coinId] = {};
    snapshots[coinId][dateISO] = price;
    resolved++;
  }

  return {
    snapshots,
    requested: uniquePairs.length,
    resolved,
    missed: missedPairs.length,
    totalFailure: uniquePairs.length > 0 && resolved === 0,
    missedPairs,
  };
}

/**
 * Clear the on-device cache. Exposed as an escape hatch for users
 * who suspect bad cached prices. Wired to a small "Vider le cache"
 * button in the UI (V2.E.UI.4).
 */
export function clearHistoricalPriceCache(): void {
  memoryCache = {};
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // No-op.
  }
}

/** Test seam — lets unit tests inject a known cache state. */
export function _setCacheForTests(cache: Record<string, number> | null): void {
  memoryCache = cache;
}
